import type { Manifest, MediaItem } from './types.js';

/**
 * A stand-in manifest shaped like the real one, used by tests and by the
 * viewer until the pipeline publishes the real release. Counts and times come
 * from the real clip sidecars (docs/FEASIBILITY.md); photo counts per chapter
 * are estimates from every tenth photo, and the imagery is a placeholder.
 */
const CHAPTERS: Array<[string, string, number, number]> = [
  ['09:37:11', '10:14:00', 58, 50],
  ['10:15:00', '10:59:00', 39, 20],
  ['11:00:00', '11:44:00', 87, 80],
  ['11:45:00', '12:29:00', 62, 90],
  ['12:30:00', '13:14:00', 60, 110],
  ['13:15:00', '13:59:00', 59, 110],
  ['14:00:00', '14:59:00', 22, 60],
  ['15:00:00', '15:34:44', 36, 155],
];

const DAY = '2026-08-26';
const OFFSET = '+05:30';

function seeded(seed: number) {
  let state = (seed * 7919) % 233280;
  return () => {
    state = (state * 9301 + 49297) % 233280;
    return state / 233280;
  };
}

/** Soft, low chroma stand-in so a placeholder reads as a photograph. */
export function standIn(seed: number, dark = false): string {
  const random = seeded(seed + 3);
  const hue = Math.round(48 + random() * 34);
  const base = (dark ? 0.4 : 0.52) + random() * 0.1;
  const lift = (base + 0.18 + random() * 0.05).toFixed(3);
  const shade = Math.max(0.15, base - 0.21).toFixed(3);
  const px = Math.round(25 + random() * 50);
  const py = Math.round(16 + random() * 32);
  return (
    `radial-gradient(ellipse 85% 65% at ${px}% ${py}%, oklch(${lift} 0.045 ${hue} / 0.85), transparent 62%), ` +
    `radial-gradient(ellipse 125% 85% at 50% 118%, oklch(${shade} 0.03 ${hue - 8} / 0.8), transparent 62%), ` +
    `linear-gradient(192deg, oklch(${(base + 0.05).toFixed(3)} 0.035 ${hue + 6}), oklch(${(base - 0.1).toFixed(3)} 0.03 ${hue - 6}))`
  );
}

function iso(time: string, addSeconds = 0): string {
  const base = Date.parse(`${DAY}T${time}${OFFSET}`);
  return new Date(base + addSeconds * 1000).toISOString();
}

export function fixtureManifest(): Manifest {
  const items: MediaItem[] = [];
  let photoNo = 0;
  let clipNo = 0;

  CHAPTERS.forEach(([start, end, clips, photos], chapterIndex) => {
    const startMs = Date.parse(`${DAY}T${start}${OFFSET}`);
    const endMs = Date.parse(`${DAY}T${end}${OFFSET}`);
    // Keep the fixture light: a sample of each chapter, spread across its span.
    const sampledPhotos = Math.min(photos, 14);
    const sampledClips = Math.min(clips, 5);
    const total = sampledPhotos + sampledClips;
    for (let i = 0; i < total; i++) {
      const isClip = i % 4 === 3 && clipNo < sampledClips * (chapterIndex + 1);
      const at = new Date(startMs + ((endMs - startMs) * i) / Math.max(1, total - 1));
      const seed = chapterIndex * 100 + i;
      const portrait = i % 3 === 0;
      if (isClip) {
        clipNo++;
        items.push({
          id: `k${String(clipNo).padStart(4, '0')}`,
          kind: 'clip',
          capturedAt: at.toISOString(),
          camera: 'ILCE-7SM3',
          filename: `C${2238 + clipNo}.MP4`,
          width: 3840,
          height: 2160,
          durationSec: 6 + (seed % 9) * 3,
          thumb: standIn(seed),
          renditions: [
            { width: 540, key: `media/clip-${clipNo}-540.mp4`, type: 'video/mp4' },
            { width: 1080, key: `media/clip-${clipNo}-1080.mp4`, type: 'video/mp4' },
            { width: 1280, key: `media/clip-${clipNo}-poster-1280.avif`, type: 'image/avif' },
            { width: 640, key: `media/clip-${clipNo}-poster-640.avif`, type: 'image/avif' },
            { width: 640, key: `media/clip-${clipNo}-poster-640.jpeg`, type: 'image/jpeg' },
          ],
        });
      } else {
        photoNo++;
        items.push({
          id: `p${String(photoNo).padStart(4, '0')}`,
          kind: 'photo',
          capturedAt: at.toISOString(),
          camera: 'ILCE-7RM5',
          filename: `DSC0${1706 + photoNo}.JPG`,
          width: portrait ? 6336 : 9504,
          height: portrait ? 9504 : 6336,
          pick: photoNo % 7 === 0,
          rating: photoNo % 11 === 0 ? 5 : undefined,
          thumb: standIn(seed),
          renditions: [400, 800, 1200, 1800, 2600].map((width) => ({
            width,
            key: `media/photo-${photoNo}-${width}.avif`,
            type: 'image/avif' as const,
          })),
          people: photoNo % 3 === 0 ? ['anusha'] : photoNo % 5 === 0 ? ['rajath'] : undefined,
        });
      }
    }
  });

  const chapters = CHAPTERS.map(([start, end], index) => {
    const startMs = Date.parse(`${DAY}T${start}${OFFSET}`);
    const endMs = Date.parse(`${DAY}T${end}${OFFSET}`);
    const inRange = items.filter((item) => {
      const t = Date.parse(item.capturedAt);
      return t >= startMs && t <= endMs;
    });
    return {
      id: `c${index + 1}`,
      startsAt: iso(start),
      endsAt: iso(end),
      photoCount: CHAPTERS[index]![3],
      clipCount: CHAPTERS[index]![2],
      coverId: inRange.find((i) => i.kind === 'photo')?.id,
      itemIds: inRange.map((i) => i.id),
    };
  });

  return {
    schemaVersion: 1,
    releaseId: 'fixture',
    event: {
      title: 'Seemantham',
      subtitle: 'Anusha and Rajath',
      date: 'Wednesday, 26 August 2026',
      startsAt: iso('09:37:11'),
      endsAt: iso('15:34:44'),
    },
    counts: { photos: 675, clips: 423 },
    chapters,
    people: [
      { id: 'anusha', name: 'Anusha', photoCount: items.filter((i) => i.people?.includes('anusha')).length },
      { id: 'rajath', name: 'Rajath', photoCount: items.filter((i) => i.people?.includes('rajath')).length },
    ],
    items,
  };
}
